import ClientEditor from '@/routes/clients/client/ClientEditor'
import { azienda, Editableazienda } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { ActionFunction, defer, LoaderFunction } from 'react-router-typesafe'
import { RoleRoute, RouteFunctionParams } from '@/types'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { redirect } from 'react-router-dom'
import { Roles } from '@/auth/azure/Roles'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { ODataClientError } from '@odata2ts/http-client-api'

export const clientEditorRouteLoader = (async ({ params }: RouteFunctionParams<'clientId'>) => {
  const validClientId = !Number.isNaN(Number(params.clientId))
  const qsAdminApi = useQsAdminApiManager.getState().service
  const today = new Date().toISOString()

  const storicoAgenti = validClientId
    ? await qsAdminApi.storico_agente().query((builder, qIStoricoAgente) => {
        builder.filter(qIStoricoAgente.azienda.props.id.eq(+params.clientId!)).orderBy(qIStoricoAgente.data.asc())
        builder.expanding('agente', (agenteBuilder) => {
          agenteBuilder.expanding('commerciale_qs', (builder) => {
            builder.select('id', 'fullname')
          })
        })
      })
    : undefined

  const clientData = validClientId
    ? await qsAdminApi
        .azienda(+params.clientId!)
        .query((builder) => {
          builder.expanding('agente', (builder) => {
            builder.expanding('commerciale_qs', (builder) => {
              builder.select('id', 'nome')
            })
          })
          builder.expanding('rivenditore', (builder) => {
            builder.select('id', 'nome')
          })
          builder.expanding('tipologia_rapporto', (builder) => {
            builder.select('id', 'nome')
          })
          builder.expanding('tipologia_pagamento', (builder) => {
            builder.select('id', 'nome')
          })
          builder.expanding('gruppo_aziendale', (builder) => {
            builder.select('id', 'nome')
          })
          builder.expanding('sedi', (sediBuilder, sede) => {
            sediBuilder.select('id')
          })
        })
        .catch((error: ODataClientError) => {
          if (error.status === 404) throw new Error('404 Not Found: Azienda non trovata')
          throw new Error()
        })
    : { data: { rapp_inizio: today } as Editableazienda }

  return defer({
    getGruppoAziendale: qsAdminApi.gruppo_aziendale().query((builder) => {
      builder.select('id', 'nome')
    }),
    getRivenditore: qsAdminApi.azienda().query((builder, qIclient) => {
      builder.select('id', 'nome')
      builder.filter(qIclient.is_rivenditore.eq(true)).orderBy(qIclient.nome.asc())
    }),
    getAgente: qsAdminApi.agente().query((builder, qIAgente) => {
      builder.filter(qIAgente.attivo.eq(true))
      builder.expanding('commerciale_qs', (builder) => {
        builder.select('id', 'fullname')
      })
    }),
    getTipologiaRapporto: qsAdminApi.tipologia_rapporto_azienda().query((builder) => {
      builder.select('id', 'nome')
    }),
    getTipologiaPagamento: qsAdminApi.tipologia_pagamento().query((builder) => {
      builder.select('id', 'nome')
    }),
    clientData,
    clientId: validClientId ? +params.clientId! : undefined,
    ...(validClientId && {
      agentHistoryGraphData: storicoAgenti?.data.value.map((agentHistory, index, elements) => {
        return {
          nome_agente: agentHistory.agente?.commerciale_qs?.fullname,
          start: new Date(agentHistory.data ?? ''),
          end: index < elements.length - 1 ? new Date(storicoAgenti.data.value[index + 1].data ?? '') : new Date(),
        }
      }),
    }),
    getContattiAziendali: validClientId
      ? qsAdminApi.contatto_aziendale().query((builder, contatto) => {
          builder.expanding('emails', (emailsBuilder, email) => {
            emailsBuilder.select('id', 'email')
          })
          builder.filter(contatto.sede.props.azienda.props.id.eq(Number(params.clientId)))
          builder.select('id', 'fullname', 'emails')
          builder.orderBy(contatto.cognome.asc())
        })
      : undefined,
    getUserInfo: getAzureUserInformation(),
    defaultCRUDAllowedRoles: [
      Roles.GlobalAdministrator,
      Roles.Administrator,
      Roles.Supervisor,
      Roles.Sales,
      Roles.ExternalSales,
    ],
  })
}) satisfies LoaderFunction

export const clientEditorRouteAction = (async ({ request }: RouteFunctionParams) => {
  const clientData = (await request.json()) as azienda
  switch (request.method) {
    case 'POST': {
      const createdClient = await useQsAdminApiManager.getState().service.azienda().create(clientData)
      return redirect(`./../${createdClient.data.id}/details`)
    }
    case 'PUT':
    case 'PATCH': {
      return await useQsAdminApiManager.getState().service.azienda(clientData.id).update(clientData)
    }
    default: {
      throw new Response('Method not allowed', {
        status: 405,
        statusText: 'Method not allowed',
      })
    }
  }
}) satisfies ActionFunction

export const ClientEditorRoute = {
  path: 'details',
  element: <ClientEditor creating={false} />,
  loader: clientEditorRouteLoader,
  action: clientEditorRouteAction,
  faIcon: faInfoCircle,
  text: 'Dettagli',
  handle: {
    crumb: (match) => ({
      label: 'Dettagli',
    }),
  },
  allowedRoles: [Roles.Guest],
} satisfies RoleRoute
